import { useCallback, useEffect, useState } from "react";
import MenuTop from "../../../components/menu/top/menu-top";
import storage from "../../../shared/services/storage";
import Form from "../../../components/form/form";
import financialFormData from "../../../features/financial/forms/financial-form-data";
import { fetchTiposLancamento } from "../../../features/lancamento/service/tipo-lancamento";
import InfoComponent from "../../../components/info/info";
import { FormProps } from "../../../shared/interfaces/form";
import { createRubricaService, fetchRubricasGrouped } from "../../../features/rubrica/service/rubrica";
import { User } from "../../../shared/interfaces/user";
import { fetchFinancialsByProjectIdService, setFinancialData } from "../../../features/financial/service/financeiro-service";
import { SEVERITY } from "../../../shared/interfaces/alert";
import { useNavigate, useParams } from "react-router-dom";
import { useAlert } from "../../../components/alert/alert";
import './style.css';
import { fetchBeneficiariesByIdCompanyService, fetchBeneficiariesService, fetchBeneficiaryService } from "../../../features/beneficiary/service/beneficiary-service";
import formatDateToISO from "../../../shared/utils/data-formatter";
import { toCents } from "../../../shared/utils/money";
import { CreateRubricaPayload, RubricaTypeOption } from "../../../components/modals/rubrica/create-rubrica-modal";
import { PendingRubricaOption } from "../../../components/modals/rubrica/rubrica-modal";
import {
    fetchCachedProjectService,
    fetchCachedRubricaTypesService,
    invalidateProjectSupportCache,
} from "../../../features/project/service/project-query-cache";

function Financial() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [formData, setFormData] = useState<FormProps[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [rubricaTypes, setRubricaTypes] = useState<RubricaTypeOption[]>([]);
    const [pendingRubricas, setPendingRubricas] = useState<PendingRubricaOption[]>([]);

    const handleCreatePendingRubrica = useCallback((payload: CreateRubricaPayload): PendingRubricaOption => {
        const tempId = `pending-rubrica-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const matchedType = payload.typeId
            ? rubricaTypes.find((type) => type.id === payload.typeId)
            : undefined;

        const pending: PendingRubricaOption = {
            tempId,
            name: payload.name,
            description: payload.description || payload.name,
            typeId: payload.typeId ?? null,
            typeName: matchedType?.name || 'Novas',
        };

        setPendingRubricas((current) => [...current, pending]);
        return pending;
    }, [rubricaTypes]);

    const getTiposLancamento = useCallback(async () => {
        try {
            const lancamentos = await fetchTiposLancamento();
            return lancamentos?.map(({ name, id}: any) => ({ label: name, value: id }));
        } catch (error) {
            console.error(error);
        }
    }, []);


    const getRubricasGrouped = useCallback(async () => {
        try {
            return await fetchRubricasGrouped();;
        } catch (error) {
            console.error(error);
        }
    }, []);

    const getProjectsByFinancier = useCallback(async () => {
        const userData: User =  await JSON.parse(storage.getStorage('user') || '');

        try {
            const projects = await fetchFinancialsByProjectIdService(userData.company.id as string);
            return projects?.map(({ name, id}: any) => ({ label: name, value: id }));
        } catch (error) {
            console.error(error);
        }
    }, []);

    const getBeneficiaries = useCallback(async () => {
        const userData: User =  await JSON.parse(storage.getStorage('user') || '');
        try {
            if (userData.company?.name?.toLowerCase() === 'causale') {
                return await fetchBeneficiariesService();
            }

            return await fetchBeneficiariesByIdCompanyService(userData.company.id as string);
        } catch (error) {
            console.error(error);
        }
    }, []);

    const setFormValues = useCallback( async () => {
        setIsLoading(true);
        try {
            const response = { idProject: id }
            const shouldShowProjectSelect = id ? false : true;

            const [valueLancamentos, valueRubricasGrouped, valueProjects, valueBeneficiaries, rubricaTypesResponse] = await Promise.all([
                getTiposLancamento(),
                getRubricasGrouped(),
                getProjectsByFinancier(),
                getBeneficiaries(),
                fetchCachedRubricaTypesService().catch(() => [])
            ]);

            setRubricaTypes(
                Array.isArray(rubricaTypesResponse)
                    ? rubricaTypesResponse.map((type: any) => ({ id: type.id, name: type.name }))
                    : []
            );
            setFormData(financialFormData(undefined, response, valueLancamentos, valueRubricasGrouped, valueProjects, valueBeneficiaries, shouldShowProjectSelect));
        } catch (error) {
            console.error("Erro ao carregar dados do formulário:", error);
            showAlert('Erro ao carregar dados do formulário', SEVERITY.ERROR);
        } finally {
            setIsLoading(false);
        }
    }, [getTiposLancamento, getRubricasGrouped, getProjectsByFinancier, getBeneficiaries, id, showAlert]);

    const resolveRubricaClientId = useCallback(async (projectId?: string) => {
        if (projectId) {
            const project = await fetchCachedProjectService(projectId);
            if (project?.companyId) {
                return project.companyId;
            }
        }

        const userData: User = await JSON.parse(storage.getStorage('user') || '');
        return userData?.company?.id;
    }, []);

    useEffect(() => {
        setFormValues();
    }, [setFormValues]);

    const onSubmitForm = async (data: any) => {
        setIsLoading(true);

        try {
            const updatedData = {
                ...data,
                idProject: id ? id : data.idProject,
                valor: toCents(data.valor)
            };

            const pendingRubrica = pendingRubricas.find((rubrica) => rubrica.tempId === updatedData.idRubrica);
            if (pendingRubrica) {
                const clientId = await resolveRubricaClientId(updatedData.idProject);
                const createdRubrica = await createRubricaService({
                    name: pendingRubrica.name,
                    description: pendingRubrica.description,
                    typeId: pendingRubrica.typeId ?? null,
                    clientId,
                });

                updatedData.idRubrica = createdRubrica.id;
                invalidateProjectSupportCache();
                setPendingRubricas((current) => current.filter((rubrica) => rubrica.tempId !== pendingRubrica.tempId));
            }

            const financialDataAddedFormatted = formatDateToISO(data.financialDateAdded);
            updatedData.financialDateAdded = financialDataAddedFormatted;

            await setFinancialData(updatedData, null);
            showAlert('Dados financeiros salvos com sucesso', SEVERITY.SUCCESS);
            navigate(`/projeto/${updatedData.idProject}`, { replace: true });
            
        } catch (error: any) {
            setIsLoading(false);
            showAlert(
                error.message || 'Erro ao salvar dados financeiros', 
                SEVERITY.ERROR
            );
        } finally { 
            setIsLoading(false);
        }
    }

    return (
        <div className="container-add-financial">
            <MenuTop />
            <InfoComponent />
            <div className="container-add-financial__container">
                <Form
                    data={formData}
                    title="Cadastrar nova movimentação financeira"
                    onSubmitForm={onSubmitForm}
                    onCancel={() => navigate(`/projeto-list/${id}`, { replace: true })}
                    isLoading={isLoading}
                    rubricaTypes={rubricaTypes}
                    pendingRubricas={pendingRubricas}
                    onCreatePendingRubrica={handleCreatePendingRubrica}
                />
            </div>
        </div>
    );
}

export default Financial;
